import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * A sideways scroll whose scrollbar you can always reach.
 *
 * The problem is easy to miss on a big monitor and impossible to miss on a
 * laptop. A wide table lives in a container as tall as its content, so its
 * native horizontal scrollbar sits at the *bottom of the table* — on the audit
 * log that measured twenty-one thousand pixels down the page. The columns on the
 * right exist, are reachable, and nobody can tell: no scrollbar in sight and no
 * other hint that the table is wider than the window.
 *
 * So the bar is drawn separately, pinned to the bottom of the viewport, holding
 * nothing but a spacer as wide as the real content — which gives the browser a
 * genuine scrollbar of the right proportions to render. The two are kept in
 * step, and the native one on the content is hidden so there is exactly one bar
 * rather than two that disagree.
 *
 * ## Why `position: fixed` and a portal, rather than `position: sticky`
 *
 * Sticky was the first implementation and it did not work. `overflow: hidden` on
 * an ancestor — which several of these tables have, to clip a rounded corner —
 * makes that ancestor the sticky containing block. It is exactly as tall as the
 * content, so there is no room to stick and the bar sits at the bottom of the
 * table, precisely where it was already unreachable. A browser test caught it:
 * bar bottom 21889px, window 700px.
 *
 * Fixed and portalled to the body has no ancestor to be trapped by, and no
 * future `overflow-hidden` added above can silently break it again. Same
 * technique as `Tooltip`, `HoverCard` and `NavFlyout`.
 *
 * Touch is unaffected: a swipe scrolls the content directly, and mobile
 * scrollbars are transient overlays that were never the affordance anyway.
 */
export function ScrollX({
  children,
  className = '',
}: {
  children: React.ReactNode;
  /** Applied to the content scroller, for the border and background it had. */
  className?: string;
}) {
  const content = useRef<HTMLDivElement | null>(null);
  const bar = useRef<HTMLDivElement | null>(null);

  /** Where to draw the bar, and how wide the content is. Null means draw none. */
  const [box, setBox] = useState<{ left: number; width: number; scrollWidth: number } | null>(null);

  /*
   * Which element the pointer is driving.
   *
   * Without this the two scrollers feed each other: setting scrollLeft on one
   * fires its scroll event, which sets the other, which fires again. It settles,
   * but it fights a drag and makes the bar stutter.
   */
  const driving = useRef<'content' | 'bar' | null>(null);

  const measure = useCallback(() => {
    const el = content.current;
    if (!el) return;

    const overflows = el.scrollWidth - el.clientWidth > 2;
    if (!overflows) {
      setBox(null);
      return;
    }

    const rect = el.getBoundingClientRect();
    /*
     * Only while the table is actually on screen.
     *
     * A fixed bar for a table you have scrolled past is a bar for nothing, and
     * on a page with two tables it would be ambiguous which one it drove.
     */
    const visible = rect.bottom > 0 && rect.top < window.innerHeight;
    if (!visible) {
      setBox(null);
      return;
    }

    setBox({ left: rect.left, width: el.clientWidth, scrollWidth: el.scrollWidth });
  }, []);

  useEffect(() => {
    const el = content.current;
    if (!el) return;

    measure();

    /*
     * ResizeObserver on the scroller *and* its first child.
     *
     * The scroller alone is not enough: the window can stay the same size while
     * the table inside it grows a column or loads more rows, and only the child
     * changes. Both is cheap and covers a filter changing the data.
     */
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);

    /*
     * And on scroll, because a fixed bar has to be told where its table went.
     * Coalesced to one measurement per frame — a scroll event fires far more
     * often than the screen redraws, and `getBoundingClientRect` forces layout.
     */
    let queued = false;
    const onMove = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        measure();
      });
    };

    window.addEventListener('scroll', onMove, { passive: true, capture: true });
    window.addEventListener('resize', onMove);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onMove, { capture: true });
      window.removeEventListener('resize', onMove);
    };
  }, [measure]);

  /** Keep the bar's own scroll position in step after it appears or moves. */
  useEffect(() => {
    if (box && bar.current && content.current) {
      bar.current.scrollLeft = content.current.scrollLeft;
    }
  }, [box]);

  const onContentScroll = () => {
    if (driving.current === 'bar') return;
    driving.current = 'content';
    if (bar.current && content.current) bar.current.scrollLeft = content.current.scrollLeft;
    // Released next frame rather than immediately: the mirrored write above has
    // its own scroll event still to come.
    requestAnimationFrame(() => {
      driving.current = null;
    });
  };

  const onBarScroll = () => {
    if (driving.current === 'content') return;
    driving.current = 'bar';
    if (bar.current && content.current) content.current.scrollLeft = bar.current.scrollLeft;
    requestAnimationFrame(() => {
      driving.current = null;
    });
  };

  return (
    <>
      <div
        ref={content}
        onScroll={onContentScroll}
        // `scroll-x-hidden` hides the native bar without disabling the
        // scrolling — wheel, touch and keyboard all still work, they just have
        // no visible track down there where nobody could see it.
        className={`overflow-x-auto ${box ? 'scroll-x-hidden' : ''} ${className}`}
      >
        {children}
      </div>

      {box &&
        createPortal(
          <div
            ref={bar}
            onScroll={onBarScroll}
            aria-hidden="true"
            style={{ left: box.left, width: box.width }}
            className="scroll-x-visible fixed bottom-0 z-30 overflow-x-auto overflow-y-hidden
              border-t border-border bg-card/95 backdrop-blur-sm"
          >
            <div style={{ width: box.scrollWidth, height: 1 }} />
          </div>,
          document.body,
        )}
    </>
  );
}
